// What a hull's drive delivers, per class and per era.
//
// The numbers are canon's, not ours: the `MillionOnMars/4xciv` repo beside
// this one, `data-lake/energy/06-ship-engines-combat.md` section 3.2, which
// gives every class two rates at each of its three era-states - a MOMENTARY
// burst and a SUSTAINED burn. Canon writes them in g, so they are held in g
// here and converted once, which is what keeps a row checkable against the
// table it came from.
//
// Canon gives acceleration directly; cargo and fuel mass are not modelled anywhere.
//
// Pure and dependency-free, so it strips and runs under Node.

/** Standard gravity, the g canon's table is written in. */
export const STANDARD_GRAVITY_MPS2 = 9.80665;

/** The 12-class taxonomy canon's propulsion table is indexed by. */
export type PropulsionClass =
  | 'pinnace' | 'corvette' | 'frigate' | 'destroyer' | 'cruiser' | 'battlecruiser'
  | 'battleship' | 'carrier' | 'q-ship' | 'cycler-escort' | 'lifeboat';

/** The three era-states canon states the table at. A row holds until the next opens. */
export type PropulsionEra = 2050 | 2100 | 2200;

export interface ThrustRates {
  /** The burst the drive can deliver, metres per second squared. */
  momentaryMps2: number;
  /** The rate a burn settles to and holds, metres per second squared. */
  sustainedMps2: number;
}

/** A row of canon's table, in the g it is written in. */
interface ThrustRow {
  /** Pinnace at 2100 and 2200 has no momentary figure in canon; this repeats sustainedG there. */
  momentaryG: number;
  /** Absent where canon gives a momentary figure alone; the burst is then all there is. */
  sustainedG?: number;
}

const TABLE: Record<PropulsionEra, Partial<Record<PropulsionClass, ThrustRow>>> = {
  // Era 2050, the NTR/NEP era, game start. Monitor is fixed and has no row.
  2050: {
    pinnace: { momentaryG: 0.5, sustainedG: 0.05 },
    corvette: { momentaryG: 0.4, sustainedG: 0.05 },
    frigate: { momentaryG: 0.5, sustainedG: 0.08 },
    destroyer: { momentaryG: 0.6, sustainedG: 0.1 },
    cruiser: { momentaryG: 0.7, sustainedG: 0.12 },
    carrier: { momentaryG: 0.3 },
    'q-ship': { momentaryG: 0.4 },
    'cycler-escort': { momentaryG: 0.3 },
    lifeboat: { momentaryG: 0.5, sustainedG: 0 },
  },
  // Era 2100, the early fusion era. Battleship is not commissioned until ~2150.
  2100: {
    pinnace: { momentaryG: 0.5, sustainedG: 0.5 },
    corvette: { momentaryG: 0.7, sustainedG: 0.1 },
    frigate: { momentaryG: 0.8, sustainedG: 0.15 },
    destroyer: { momentaryG: 1.0, sustainedG: 0.2 },
    cruiser: { momentaryG: 1.2, sustainedG: 0.25 },
    battlecruiser: { momentaryG: 1.0, sustainedG: 0.3 },
    carrier: { momentaryG: 0.4 },
    'q-ship': { momentaryG: 0.6 },
    'cycler-escort': { momentaryG: 0.4 },
    lifeboat: { momentaryG: 1.0, sustainedG: 0 },
  },
  // Era 2200, the mature fusion era.
  2200: {
    pinnace: { momentaryG: 0.5, sustainedG: 0.5 },
    corvette: { momentaryG: 1.5, sustainedG: 0.3 },
    frigate: { momentaryG: 2.0, sustainedG: 0.4 },
    destroyer: { momentaryG: 3.0, sustainedG: 0.6 },
    cruiser: { momentaryG: 4.0, sustainedG: 0.8 },
    battlecruiser: { momentaryG: 3.5, sustainedG: 1.0 },
    battleship: { momentaryG: 3.0, sustainedG: 0.8 },
    carrier: { momentaryG: 0.8 },
    'q-ship': { momentaryG: 1.5, sustainedG: 0.3 },
    'cycler-escort': { momentaryG: 0.6 },
    lifeboat: { momentaryG: 1.0 },
  },
};

/**
 * The era-state a calendar year sits in. Canon states the table at 2050, 2100
 * and 2200, and each row holds until the next era opens, so a mission in 2078
 * flies the 2050 row rather than an interpolation nobody wrote down.
 */
export function eraForYear(year: number): PropulsionEra {
  if (year >= 2200) return 2200;
  if (year >= 2100) return 2100;
  return 2050;
}

/**
 * Canon's two rates for a class in an era, in metres per second squared.
 * Throws on a class canon gives no drive at that era - a monitor is bolted to
 * its base and a battleship does not exist before 2150, and either would be a
 * silent zero otherwise.
 */
export function thrustRates(cls: PropulsionClass, era: PropulsionEra): ThrustRates {
  const row = TABLE[era][cls];
  if (row === undefined) {
    throw new Error(`no propulsion row in canon for ${cls} at era ${era}`);
  }
  return {
    momentaryMps2: row.momentaryG * STANDARD_GRAVITY_MPS2,
    sustainedMps2: (row.sustainedG ?? row.momentaryG) * STANDARD_GRAVITY_MPS2,
  };
}

/**
 * The player's hull. The Iuventius Pup is the Gens Iovia signature CORVETTE
 * (`../4xciv/07-living-universe.md` section G.5.8b.i, and the Iovian corvette
 * template in `data-lake/water/03-multi-use-shielding-cryogenic-industrial.md`).
 * The `CL` it carries in `src/shipClasses.ts` and in the damage schematic is
 * the SFB hull-class taxonomy the SSD is drawn from, which is a different
 * scale from the 12-class propulsion taxonomy this table is indexed by.
 */
export const PLAYER_PROPULSION_CLASS: PropulsionClass = 'corvette';
